import axios from 'axios';
import { isJwtExpired } from '@utils/jwtUtils';

const PHONE_SYSTEM_API_URL = process.env.REACT_APP_PHONE_SYSTEM_API_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: `${PHONE_SYSTEM_API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Twilio token helpers
export const setTwilioToken = (token) => {
  if (token) {
    localStorage.setItem('twilioToken', token);
  }
};

export const getTwilioToken = () => {
  return localStorage.getItem('twilioToken');
};

// Request interceptor - add JWT token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('jwtToken');
    if (token) {
      // Preempt expired tokens client-side so the user sees a clean redirect
      // instead of a mid-call 401 from the server.
      if (isJwtExpired(token)) {
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('twilioToken');
        window.dispatchEvent(new CustomEvent('unauthorized', { detail: { status: 401, reason: 'expired' } }));
        window.location.href = '/login';
        return Promise.reject(new axios.Cancel('Token expired'));
      }
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Phone System API Error:', error.response?.data || error.message);
    if (error.response?.status === 401) {
      // Clear auth data and redirect to login
      localStorage.removeItem('jwtToken');
      localStorage.removeItem('twilioToken');
      // Dispatch event for auth context to handle logout
      window.dispatchEvent(new CustomEvent('unauthorized', { detail: { status: 401 } }));
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const login = async (email, password) => {
  console.log('[API] Sending login request...');
  const response = await api.post('/auth/login', {
    email,
    password,
  });
  console.log('[API] Login response received:', response.data);
  console.log('[API] Full payload:', JSON.stringify(response.data, null, 2));
  return response.data;
};

export const logout = async () => {
  localStorage.removeItem('jwtToken');
  localStorage.removeItem('twilioToken');
};

// Outreach search endpoints
export const searchOutreach = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.startDate) params.append('startDate', options.startDate.toISOString());
  if (options.endDate) params.append('endDate', options.endDate.toISOString());
  if (options.outreachType) params.append('outreachType', options.outreachType);
  if (options.phoneNumber) params.append('phoneNumber', options.phoneNumber);
  if (options.callSid) params.append('callSid', options.callSid);
  if (options.patientName) params.append('patientName', options.patientName);
  if (options.coachName) params.append('coachName', options.coachName);
  if (options.category) params.append('category', options.category);
  if (options.dispositionReason) params.append('dispositionReason', options.dispositionReason);
  if (options.companyId) params.append('companyId', options.companyId);
  if (options.direction) params.append('direction', options.direction);
  if (options.page) params.append('page', options.page);
  if (options.pageSize) params.append('pageSize', options.pageSize || 25);
  const response = await api.get(`/outreach/search?${params.toString()}`);
  return response.data;
};

export const getOutreachDetail = async (contactId) => {
  const response = await api.get(`/outreach/${contactId}/detail`);
  return response.data;
};

export const getOutreachFormAttributes = async () => {
  const response = await api.get('/outreach/form-attributes');
  return response.data;
};

// Resolves a short-lived, playable URI for an outreach recording.
// Returns { uri, expiresAtUtc, source }.
export const getOutreachRecordingUri = async (contactId) => {
  const response = await api.get(`/outreach/${contactId}/recording-uri`);
  return response.data;
};

// Patient search (outgoing phone controls) — search by phone number or email.
// Returns { patients: [{ guid, name, accountNumber, companyName, companyId, phoneNumber, email, active }], totalCount, truncated }
export const searchPatients = async (query) => {
  const params = new URLSearchParams({ query });
  const response = await api.get(`/patients/search?${params.toString()}`);
  return response.data;
};

// Error tracking endpoints
export const getPhoneErrors = async (options = {}) => {
  const params = new URLSearchParams();

  if (options.startDate) params.append('startDate', options.startDate.toISOString());
  if (options.endDate) params.append('endDate', options.endDate.toISOString());
  if (options.category) params.append('category', options.category);
  if (options.severity) params.append('severity', options.severity);
  if (options.callSid) params.append('callSid', options.callSid);
  if (options.page) params.append('page', options.page);
  if (options.pageSize) params.append('pageSize', options.pageSize || 50);
  if (options.includeStatistics !== undefined) params.append('includeStatistics', options.includeStatistics);

  const response = await api.get(`/errors/filter?${params.toString()}`);
  return response.data;
};

export const getPhoneErrorById = async (errorId) => {
  const response = await api.get(`/errors/${errorId}`);
  return response.data;
};

export const getPhoneErrorStatistics = async (options = {}) => {
  const params = new URLSearchParams();

  if (options.startDate) params.append('startDate', options.startDate.toISOString());
  if (options.endDate) params.append('endDate', options.endDate.toISOString());

  const response = await api.get(`/errors/statistics?${params.toString()}`);
  return response.data;
};

export const getCallErrorHistory = async (callSid) => {
  const response = await api.get(`/errors/call/${callSid}`);
  return response.data;
};

// Hang up an active call via the backend (terminates via Twilio REST API + records end time).
export const hangUpCall = async (callSid) => {
  const response = await api.post('/twilio/outgoing/hang-up', { callSid });
  return response.data;
};

// Voicemail endpoints
export const sendToVoicemail = async (callSid) => {
  const response = await api.post('/twilio/outgoing/send-to-voicemail', { callSid });
  return response.data;
};

// IVR call initiation.
// overrideDailyLimit (testing only) bypasses the once-per-day call guard; the server honors it
// only in local/dev/qa and ignores it in stage/prod. productLineId: 1 = CPAP (server default), 2 = CGM.
export const initiateIvrCall = async ({ patientGuid, phoneNumber, overrideDailyLimit = false, productLineId }) => {
  const response = await api.post('/twilio/ivr/resupply/initiate', {
    patientGuid,
    phoneNumber,
    overrideDailyLimit,
    ...(productLineId ? { productLineId } : {}),
  });
  return response.data;
};

// Team queue monitoring
export const getTeamQueues = async () => {
  const response = await api.get('/teams/queues');
  return response.data;
};

// Answer the next queued caller in a specific team's queue.
// Coach identity is read from the JWT by the backend — only the target queue name is sent.
// The backend validates the coach is eligible to answer that queue type.
export const answerQueueCall = async ({ queueName }) => {
  const response = await api.post('/queue/answer', { queueName });
  return response.data;
};

// Call Experience (admin) — api/call-experience
// `companyId` absent = the global default; a parent group's id = the group; otherwise a company.

const cxParams = (values) => {
  const params = {};
  Object.entries(values).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== '') params[k] = v;
  });
  return params;
};

const cxContentPath = (kind, key) => `/call-experience/content/${encodeURIComponent(kind)}/${encodeURIComponent(key)}`;

export const getCallExperienceCompanies = async () => {
  const response = await api.get('/call-experience/companies');
  return response.data;
};

export const getCompanySettingsSummary = async (companyId) => {
  const response = await api.get(`/call-experience/companies/${companyId}/settings`);
  return response.data;
};

export const getInboundTest = async (companyId) => {
  const response = await api.get(`/call-experience/companies/${companyId}/test-call/inbound`);
  return response.data;
};

export const searchTestPatients = async (companyId, query) => {
  const response = await api.get(`/call-experience/companies/${companyId}/test-call/patients`, { params: { query } });
  return response.data;
};

export const getRecentTestCalls = async (companyId) => {
  const response = await api.get(`/call-experience/companies/${companyId}/test-call/calls`);
  return response.data;
};

// One of `contactId` / `callSid` identifies the call.
export const getTestCallInfo = async (companyId, { contactId, callSid }) => {
  const response = await api.get(`/call-experience/companies/${companyId}/test-call/call-info`, {
    params: contactId ? { contactId } : { callSid },
  });
  return response.data;
};

export const getGlobalCallSettings = async () => {
  const response = await api.get('/call-experience/settings');
  return response.data;
};

export const getJourneys = async () => {
  const response = await api.get('/call-experience/journeys');
  return response.data;
};

// language: 'English' | 'Spanish'
export const getJourney = async ({ journeyId, productLineId, companyId, language }) => {
  const response = await api.get(`/call-experience/journeys/${encodeURIComponent(journeyId)}`, {
    params: cxParams({ productLineId, companyId, language }),
  });
  return response.data;
};

export const getContentLibrary = async ({ companyId, language }) => {
  const response = await api.get('/call-experience/content', { params: cxParams({ companyId, language }) });
  return response.data;
};

// kind: 'builtin' | 'template' | 'question'
export const getContentDetail = async ({ kind, key, productLineId, companyId }) => {
  const response = await api.get(cxContentPath(kind, key), { params: cxParams({ productLineId, companyId }) });
  return response.data;
};

// text: { en, es }
export const saveContent = async ({ kind, key, productLineId, companyId, en, es }) => {
  const response = await api.put(cxContentPath(kind, key), { en, es }, { params: cxParams({ productLineId, companyId }) });
  return response.data;
};

export const removeContentOverride = async ({ kind, key, productLineId, companyId }) => {
  const response = await api.delete(cxContentPath(kind, key), { params: cxParams({ productLineId, companyId }) });
  return response.data;
};

export const saveTemplateVariant = async ({ key, id, en, es }) => {
  const response = await api.put(`${cxContentPath('template', key)}/variants/${id}`, { en, es });
  return response.data;
};

export const removeTemplateVariant = async ({ key, id }) => {
  const response = await api.delete(`${cxContentPath('template', key)}/variants/${id}`);
  return response.data;
};

export const setToggle = async ({ identifier, companyId, enabled }) => {
  const response = await api.put(`/call-experience/toggles/${encodeURIComponent(identifier)}`, { enabled }, {
    params: cxParams({ companyId }),
  });
  return response.data;
};

export default api;
